import { motion } from 'framer-motion';
import { BarChart4, Handshake, Settings, Shield, ShoppingCart, TruckIcon } from 'lucide-react';
import React, { memo } from 'react';
import { Autoplay, Grid, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ElementAnimation from '@components/animation/ElementAnimation';

const BREAKPOINTS = {
  976: {
    slidesPerView: 3,
    spaceBetween: 20,
    grid: {
      rows: 2,
      fill: 'row' as const,
    },
  },
  480: {
    slidesPerView: 2,
    spaceBetween: 20,
    grid: {
      rows: 2,
      fill: 'row' as const,
    },
  },
};

const SERVICES = [
  {
    key: 1,
    icon: <Handshake className="h-10 w-10 text-orange-600" />,
    title: 'Hợp tác sản xuất',
    description: 'Hợp tác sản xuất nông sản thực phẩm theo tiêu chuẩn VietGap',
  },
  {
    key: 2,
    icon: <Settings className="h-10 w-10 text-orange-600" />,
    title: 'Sơ chế, đóng gói',
    description: 'Sơ chế, chế biến, đóng gói, giao vận theo tiêu chuẩn ISO22000:2018t',
  },
  {
    key: 3,
    icon: <TruckIcon className="h-10 w-10 text-orange-600" />,
    title: 'Phân phối thực phẩm',
    description: 'Cung cấp thực phẩm an toàn, đáp ứng nhu cầu F&B trong nước và quốc tế',
  },
  {
    key: 4,
    icon: <Shield className="h-10 w-10 text-orange-600" />,
    title: 'Đảm bảo chất lượng',
    description: 'Có nguồn gốc, có chứng nhận đạt chuẩn an toàn thực phẩm',
  },
  {
    key: 5,
    icon: <ShoppingCart className="h-10 w-10 text-orange-600" />,
    title: 'Đặt hàng trực tuyến',
    description: 'Dễ dàng đặt hàng trực tuyến với giao diện thân thiện, nhanh chóng',
  },
  {
    key: 6,
    icon: <BarChart4 className="h-10 w-10 text-orange-600" />,
    title: 'Giá cả cạnh tranh',
    description: 'Giá cả hợp lý với nhiều ưu đãi đặc biệt dành cho khách hàng thân thiết',
  },
];

function SectionService() {
  return (
    <section id="services" className="py-8 md:py-16 bg-gradient-to-b from-orange-50 to-white-50/70">
      <div className="container-content">
        <ElementAnimation className="text-center mb-12 w-full">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Dịch vụ của chúng tôi</h2>
          <p className="text-muted-foreground mt-4 text-sm md:text-base max-w-[700px] mx-auto">
            Thực phẩm cho bếp ăn công nghiệp, trường học, nhà hàng, khách sạn
          </p>
        </ElementAnimation>
        <ElementAnimation>
          <Swiper
            observer={true}
            observeParents={true}
            updateOnWindowResize={true}
            watchSlidesProgress={true}
            modules={[Navigation, Pagination, Keyboard, Autoplay, Grid]}
            grabCursor
            grid={{
              rows: 2,
              fill: 'row',
            }}
            spaceBetween={20}
            breakpoints={BREAKPOINTS}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            className="relative !pb-10 md:pb-4 w-full"
            pagination={{
              clickable: true,
              dynamicMainBullets: 4,
            }}
          >
            {SERVICES.map((service) => (
              <SwiperSlide
                key={service.key}
                className="rounded-lg bg-card shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="h-full text-start">
                  <div className="relative flex flex-col space-y-1.5 p-6 pb-2">
                    <motion.div
                      className="mb-4"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                      {service.icon}
                    </motion.div>
                    <h3 className="text-2xl font-semibold leading-none tracking-tight">
                      {service.title}
                    </h3>
                  </div>
                  <div className="p-6 pt-0">
                    <p className="text-sm text-muted-foreground">{service.description}</p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </ElementAnimation>
      </div>
    </section>
  );
}

export default memo(SectionService);
